import { useState } from 'react'
import type { FormEvent } from 'react'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { AlertCircle, ExternalLink, Loader2, LockKeyhole, ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { HoldConfirmButton } from '@/components/ui/hold-confirm-button'
import { humanizeParticipantPackageError } from '../services/participantPackage.service'
import { formatUserFacingError, withErrorCode } from '@/lib/errors'
import type {
  ParticipantCheckoutHandoff,
  ParticipantPaymentStatus,
} from '../types'
import { formatDateTime, formatParticipantMoney } from './ParticipantPackageSummary'
import { getStripePromise } from '@/lib/stripe'

interface ParticipantPackagePaymentProps {
  priceCents: number
  currency: string
  policyText: string
  paymentDueAt?: string
  paymentExpiryAt?: string
  hardCutoffAt?: string
  paymentStatus?: ParticipantPaymentStatus
  failureMessage?: string
  disabled?: boolean
  onCreateCheckout: (idempotencyKey: string, acknowledged: boolean) => Promise<ParticipantCheckoutHandoff>
  onPaymentSubmitted: () => void | Promise<unknown>
}

/**
 * Payment boundary for the participant flow. The backend may return a hosted
 * Checkout URL or a PaymentIntent client secret; neither path handles card
 * data in the application server or stores it in Firestore.
 */
export function ParticipantPackagePayment({
  priceCents,
  currency,
  policyText,
  paymentDueAt,
  paymentExpiryAt,
  hardCutoffAt,
  paymentStatus,
  failureMessage,
  disabled = false,
  onCreateCheckout,
  onPaymentSubmitted,
}: ParticipantPackagePaymentProps) {
  const [handoff, setHandoff] = useState<ParticipantCheckoutHandoff | null>(null)
  const [isStarting, setIsStarting] = useState(false)
  const [paymentSubmitted, setPaymentSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [checkoutAcknowledged, setCheckoutAcknowledged] = useState(false)
  const [idempotencyKey] = useState(() => crypto.randomUUID())

  async function beginCheckout() {
    if (disabled || isStarting || !checkoutAcknowledged) return
    setIsStarting(true)
    setError(null)

    try {
      const nextHandoff = await onCreateCheckout(idempotencyKey, checkoutAcknowledged)
      if (nextHandoff.mode === 'hosted') {
        window.location.assign(nextHandoff.url)
        return
      }
      setHandoff(nextHandoff)
    } catch (err) {
      setError(humanizeParticipantPackageError(err))
    } finally {
      setIsStarting(false)
    }
  }

  if (paymentSubmitted) {
    return (
      <div className="flex items-start gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5 text-sm">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" />
        <div>
          <p className="font-semibold">Payment submitted securely</p>
          <p className="mt-1 text-muted-foreground">
            Stripe is confirming your payment. We&apos;ll issue your tickets after the server receives
            the confirmation. You can safely close this page.
          </p>
        </div>
      </div>
    )
  }

  if (handoff?.mode === 'client_secret') {
    return (
      <div className="space-y-4 rounded-xl border bg-card p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <LockKeyhole className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
          <div>
            <h3 className="font-semibold">Complete your secure payment</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Pay exactly {formatParticipantMoney(priceCents, currency)}. Your card details go
              directly to Stripe.
            </p>
          </div>
        </div>
        <Elements
          stripe={getStripePromise()}
          options={{ clientSecret: handoff.clientSecret, appearance: { theme: 'stripe' } }}
        >
          <ClientSecretPaymentForm
            amountCents={priceCents}
            currency={currency}
            onPaymentSubmitted={async () => {
              setPaymentSubmitted(true)
              await onPaymentSubmitted()
            }}
          />
        </Elements>
      </div>
    )
  }

  const paymentFailed = paymentStatus === 'payment_failed' || paymentStatus === 'payment_expired'

  return (
    <div className="space-y-4 rounded-xl border bg-card p-5 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
          <div>
            <h3 className="font-semibold">
              {paymentStatus === 'pending' ? 'Continue your secure payment' : 'Ready to pay?'}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {paymentStatus === 'pending'
                ? 'Return to the existing Stripe Checkout session. If payment is already processing, no new charge will be created.'
                : 'One secure Stripe payment reserves your participant package.'}
            </p>
          </div>
        </div>
        <p className="text-xl font-semibold sm:text-right">
          {formatParticipantMoney(priceCents, currency)}
        </p>
      </div>

      {paymentDueAt && (
        <p className="text-sm text-muted-foreground">
          Payment is due by <strong className="text-foreground">{formatDateTime(paymentDueAt)}</strong>.
          {paymentExpiryAt && (
            <> Your reserved spot is forfeited after <strong className="text-foreground">{formatDateTime(paymentExpiryAt)}</strong></>
          )}
          {hardCutoffAt && (
            <>, and checkout always closes by the show cutoff at <strong className="text-foreground">{formatDateTime(hardCutoffAt)}</strong></>
          )}.
        </p>
      )}

      {paymentFailed && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" role="alert">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            {withErrorCode(
              failureMessage ??
                (paymentStatus === 'payment_expired'
                  ? 'Your previous checkout expired. You can start a new secure payment attempt before the deadline.'
                  : 'Your previous payment attempt did not complete. You can try again before the deadline.'),
              paymentStatus === 'payment_expired'
                ? 'payments/previous-checkout-expired'
                : 'payments/previous-attempt-failed',
            )}
          </span>
        </div>
      )}

      {paymentStatus === 'pending' && (
        <div className="flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm">
          <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-primary" aria-hidden="true" />
          <span>
            Stripe is confirming or holding your existing checkout. Continuing reopens that same
            session and does not create a second order.
          </span>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 text-sm text-destructive" role="alert">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-start gap-3 rounded-lg border p-4">
        <Checkbox
          id="participant-non-refundable-checkout"
          checked={checkoutAcknowledged}
          onCheckedChange={(checked) => setCheckoutAcknowledged(checked === true)}
          disabled={disabled || isStarting}
          required
          aria-required="true"
        />
        <label
          htmlFor="participant-non-refundable-checkout"
          className="cursor-pointer text-sm leading-6"
        >
          {policyText}{' '}
          I also agree to the{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4">
            Terms of Service
          </a>{' '}
          and acknowledge the{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4">
            Privacy Policy
          </a>
          .
        </label>
      </div>

      <Button
        type="button"
        size="lg"
        className="w-full"
        onClick={() => void beginCheckout()}
        disabled={disabled || isStarting || !checkoutAcknowledged}
      >
        {isStarting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <ExternalLink className="h-4 w-4" aria-hidden="true" />}
        {isStarting
          ? 'Preparing secure checkout…'
          : paymentStatus === 'pending'
            ? 'Continue secure checkout'
            : `Pay ${formatParticipantMoney(priceCents, currency)}`}
      </Button>
    </div>
  )
}

function ClientSecretPaymentForm({
  amountCents,
  currency,
  onPaymentSubmitted,
}: {
  amountCents: number
  currency: string
  onPaymentSubmitted: () => void | Promise<unknown>
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submitPayment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!stripe || !elements || processing) return

    setProcessing(true)
    setError(null)

    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      })
      if (result.error) {
        setError(formatUserFacingError(
          result.error,
          'Payment could not be completed. Please check your payment details and try again.',
          'payments/card-confirmation',
        ))
        return
      }
      await onPaymentSubmitted()
    } catch (err) {
      setError(humanizeParticipantPackageError(err))
    } finally {
      setProcessing(false)
    }
  }

  return (
    <form onSubmit={(event) => void submitPayment(event)} className="space-y-4">
      <PaymentElement />
      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
      <HoldConfirmButton type="submit" disabled={!stripe || !elements || processing} className="w-full">
        {processing ? 'Processing payment…' : `Pay ${formatParticipantMoney(amountCents, currency)}`}
      </HoldConfirmButton>
      <p className="text-center text-xs text-muted-foreground">
        Payment is final and non-refundable. Stripe will provide your receipt.
      </p>
    </form>
  )
}
